import type { ApolloError } from '@apollo/client';
import { order_by } from '../lib/generated/zeus';
import { useTypedQuery } from '../lib/generated/zeus/apollo';
import { activeRecord } from '../lib/graphql/common/queries';
import { genericScalars } from '../lib/graphql/helpers';
import { teamRoleSelector } from './selectors/team';
import type { StoredTeamRole } from './types';

export const teamRolesQueryName = 'queryTeamRoles';

/**
 * Provides GraphQL query to fetch available Team Roles.
 * If this query has been called previously, the value will be read from the cache instead of in a new network request.
 * @param onError custom error handling.
 * @returns custom hook that provides GraphQL query to fetch Team Roles.
 */
const useQueryTeamRoles = (
  onError: (e: ApolloError) => void,
  onCompleted?: (data: {
    readonly team_role: readonly StoredTeamRole[];
  }) => void,
  all?: boolean,
  teamId?: string,
  externalUserId?: string
) => {
  // Filter applied to the team roles.
  // Some team roles are not available for certain team type for assignment, they are defined within permissions_team_role view.
  // ID of the team will determine the team entity with type in permissions.
  const teamRoleFilter = () => {
    const teamFilter = {
      permissions_team_role: {
        _or: [
          {
            team_id: {
              _eq: teamId,
            },
          },
          {
            // If no team_id present, it means wildcard permission exists.
            team_id: {
              _is_null: true,
            },
          },
        ],
        external_id: {
          _eq: externalUserId,
        },
      },
    };

    if (all === true) {
      return teamId !== undefined ? teamFilter : {};
    }

    if (teamId !== undefined) {
      return {
        ...activeRecord,
        ...teamFilter,
      };
    }

    return activeRecord;
  };

  const result = useTypedQuery(
    {
      team_role: [
        {
          where: teamRoleFilter(),
          order_by: [
            {
              role_group: order_by.asc,
            },
            {
              name: order_by.asc,
            },
          ],
        },
        teamRoleSelector,
      ],
    },
    {
      scalars: genericScalars,
      apolloOptions: {
        onError,
        onCompleted,
      },
      operationOptions: {
        operationName: teamRolesQueryName,
      },
    }
  );

  return result;
};

export default useQueryTeamRoles;
